/* eslint semi: 1 */
import { ref, computed } from 'vue';
import { useMediaSelectionStore } from '@/pinia/stores/media/mediaSelectionStore';
import { useMediaStore } from '@/pinia/stores/media/mediaStore';
import { useMediaListStore } from '@/pinia/stores/media/mediaListStore';
import { useMediaQueryStore } from '@/pinia/stores/media/mediaQueryStore';

/**
 * Composable for inline name editing with optimistic updates.
 */
export function useInlineNameEdit () {
  // Pinia stores
  const mediaSelectionStore = useMediaSelectionStore();
  const mediaStore = useMediaStore();
  const listStore = useMediaListStore();
  const queryStore = useMediaQueryStore();

  const selectedIds = computed(() => new Set(mediaSelectionStore.selection.map(item => item.resourceId)));
  /**
   * Resolves media items directly from the raw list-store data so that the
   * original name is always available even before `populateAllItems` has run.
   */
  const mediaItems = computed(() => {
    if (queryStore.isFiles) return listStore.filesPageMedia;
    if (queryStore.isAlert) return listStore.alertsPageMedia;
    if (queryStore.isLed) {
      const items: typeof listStore.filesPageMedia = [];
      listStore.ledMediaCanvasMap.forEach(canvas => items.push(...(canvas.media ?? [])));
      return items;
    }
    if (queryStore.isVideowall) {
      const items: typeof listStore.filesPageMedia = [];
      listStore.videowallMediaCanvasMap.forEach(canvas => items.push(...(canvas.media ?? [])));
      return items;
    }
    return [];
  });
  /**
   * Resolves media items with any staged updates overlaid on top.
   * This ensures the inline name edit UI reflects the latest confirmed
   * update value even before the staged updates have been merged into
   * the canonical list.
   * @returns {MediaListItem[]}
   */
  const resolvedMediaItemsUpdate = computed(() => {
    if (!listStore.stagedMediaUpdates?.size) return mediaItems.value;

    return mediaItems.value.map(item => {
      const update = listStore.stagedMediaUpdates.get(item.resourceId);
      return update?.resourceId && update.resourceId === item.resourceId
        ? update
        : item;
    });
  });

  const optimisticNames = computed(() => mediaStore.optimisticNames);

  // Which item (by resourceId) is currently in inline-edit mode. Only one at a time.
  const editingResourceId = ref<string | null>(null);

  /**
   * Starts inline-edit mode for a single item.
   * No-ops when multiple items are selected to avoid ambiguity.
   * @param {string} resourceId
   */
  function startEditNameMode (resourceId: string) {
    if (selectedIds.value.size > 1) return;
    editingResourceId.value = resourceId;
  }

  /**
   * Confirms the name edit on blur.
   * Sets the optimistic name in the store immediately so the UI reflects the
   * change before the API round-trip completes, then returns the rename payload
   * for the component to emit and act on.
   * Returns `null` when the edit is a no-op (empty or unchanged name).
   * @param {FocusEvent} event - The blur event from the inline input
   * @returns {{ resourceId: string | null, newName: string } | null}
   */
  function confirmEditName (event: FocusEvent): { resourceId: string | null; newName: string } | null {
    const resourceId = editingResourceId.value;
    const newName = (event.target as HTMLInputElement).value.trim();
    const originalName = resolvedMediaItemsUpdate.value.find(i => i.resourceId === resourceId)?.name;

    editingResourceId.value = null;

    if (!newName || newName === originalName) return null;

    return { resourceId, newName };
  }

  return {
    editingResourceId,
    mediaItems: resolvedMediaItemsUpdate,
    optimisticNames,

    startEditNameMode,
    confirmEditName,
  };
}
